/**
 * Platform / display-mode detection used to pick the right OAuth sign-in
 * strategy: `signInWithPopup` on desktop browsers, `signInWithRedirect` on
 * mobile browsers and installed PWAs where popups are commonly blocked or
 * open a detached window that never returns the credential.
 */

type StandaloneNavigator = Navigator & { standalone?: boolean };
type UaDataNavigator = Navigator & { userAgentData?: { mobile?: boolean } };

/** True when the app is running as an installed / standalone PWA. */
export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;

  // Modern Android (and installed desktop PWAs) expose the display mode.
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  // iOS Safari uses a non-standard navigator.standalone flag instead.
  const iosStandalone = (window.navigator as StandaloneNavigator).standalone === true;

  return displayModeStandalone || iosStandalone;
}

/** True for mobile browsers, where popup-based sign-in is unreliable. */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;

  // Prefer the structured Client Hints signal when the browser provides it.
  const uaData = (navigator as UaDataNavigator).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile;

  const ua = navigator.userAgent;
  if (/Android|iPhone|iPod|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)) {
    return true;
  }

  // iPadOS 13+ reports a desktop Safari UA string; disambiguate via touch.
  const isIpadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad/.test(ua) || isIpadOs;
}

/**
 * Redirect-based sign-in is preferred on mobile browsers and installed PWAs.
 * Desktop browsers get the popup flow (no full-page navigation).
 */
export function preferRedirectSignIn(): boolean {
  return isStandalonePwa() || isMobileBrowser();
}
